<template>
  <ContentWrap>
    <div class="page-head">
      <div>
        <h2>酒店入驻申请</h2>
        <p>提交酒店资料和专项资质，审核通过后系统自动开通酒店经营台。</p>
      </div>
      <el-button type="primary" @click="openForm()">新建申请</el-button>
    </div>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column prop="hotelName" label="酒店名称" min-width="180" />
      <el-table-column label="业态" width="100">
        <template #default="{ row }">{{ propertyTypeLabel(row.propertyType) }}</template>
      </el-table-column>
      <el-table-column prop="contactName" label="联系人" width="110" />
      <el-table-column prop="contactPhone" label="联系电话" width="150" />
      <el-table-column label="状态" width="120">
        <template #default="{ row }">
          <el-tag :type="statusMeta(row.status).type">{{ statusMeta(row.status).label }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="submitTime" label="提交时间" width="180" />
      <el-table-column label="开通结果" min-width="180">
        <template #default="{ row }">
          <span v-if="row.status === 'ACTIVE'">酒店 ID：{{ row.shopId }}</span>
          <el-text v-else-if="row.status === 'FAILED'" type="danger">
            {{ row.lastProvisionError || '开通失败，请等待平台重试' }}
          </el-text>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="190" fixed="right">
        <template #default="{ row }">
          <el-button
            v-if="editableStatuses.includes(row.status)"
            link
            type="primary"
            @click="openForm(row)"
          >
            编辑
          </el-button>
          <el-button
            v-if="row.status === 'DRAFT'"
            link
            type="success"
            @click="submitApplication(row)"
          >
            提交审核
          </el-button>
          <el-button link @click="showMaterials(row)">查看资质</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="loadList"
    />
  </ContentWrap>

  <Dialog
    v-model="formVisible"
    :title="form.id ? '编辑酒店入驻申请' : '新建酒店入驻申请'"
    width="720px"
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-width="110px">
      <el-form-item label="酒店名称" prop="hotelName">
        <el-input v-model="form.hotelName" maxlength="128" show-word-limit />
      </el-form-item>
      <el-form-item label="酒店业态" prop="propertyType">
        <el-select v-model="form.propertyType" class="!w-100%">
          <el-option
            v-for="item in propertyTypes"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="经营地址" prop="address">
        <el-input v-model="form.address" maxlength="255" show-word-limit />
      </el-form-item>
      <el-form-item label="联系人" prop="contactName">
        <el-input v-model="form.contactName" maxlength="64" />
      </el-form-item>
      <el-form-item label="联系电话" prop="contactPhone">
        <el-input v-model="form.contactPhone" maxlength="32" />
      </el-form-item>
      <el-form-item label="专项资质" required>
        <div class="materials">
          <div v-for="(material, index) in form.materials" :key="index" class="material-row">
            <el-input v-model="material.name" placeholder="材料名称，如营业执照" />
            <el-input v-model="material.url" placeholder="材料原件 URL" />
            <el-button type="danger" link @click="removeMaterial(index)">删除</el-button>
          </div>
          <el-button plain type="primary" @click="addMaterial">添加资质材料</el-button>
          <el-text type="info">至少填写一项材料名称和可访问的原件 URL。</el-text>
        </div>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="formVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="saveForm">保存草稿</el-button>
    </template>
  </Dialog>

  <Dialog v-model="materialsVisible" title="资质原件" width="620px">
    <el-empty v-if="viewMaterials.length === 0" description="没有可展示的资质材料" />
    <el-table v-else :data="viewMaterials">
      <el-table-column prop="name" label="材料名称" />
      <el-table-column label="原件" width="100">
        <template #default="{ row }">
          <el-link :href="safeMaterialUrl(row.url)" target="_blank" type="primary">查看</el-link>
        </template>
      </el-table-column>
    </el-table>
  </Dialog>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted } from 'vue'
import type { FormInstance, FormRules, TagProps } from 'element-plus'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  createMerchantOnboarding,
  getMerchantOnboardingPage,
  submitMerchantOnboarding,
  updateMerchantOnboarding,
  type HotelOnboardingApplication,
  type HotelOnboardingStatus,
  type HotelQualificationMaterial
} from '@/api/hotel/onboarding'

defineOptions({ name: 'MerchantHotelOnboarding' })

const propertyTypes = [
  { value: 0, label: '酒店' },
  { value: 1, label: '民宿' },
  { value: 2, label: '客栈' },
  { value: 3, label: '公寓' },
  { value: 4, label: '青旅' },
  { value: 5, label: '度假村' }
]
const editableStatuses: HotelOnboardingStatus[] = ['DRAFT', 'REJECTED']
const statusMap: Record<HotelOnboardingStatus, { label: string; type: TagProps['type'] }> = {
  DRAFT: { label: '草稿', type: 'info' },
  PENDING: { label: '审核中', type: 'warning' },
  APPROVED: { label: '已通过', type: 'success' },
  REJECTED: { label: '已驳回', type: 'danger' },
  PROVISIONING: { label: '开通中', type: 'warning' },
  ACTIVE: { label: '已开通', type: 'success' },
  FAILED: { label: '开通失败', type: 'danger' }
}

const loading = ref(false)
const saving = ref(false)
const list = ref<HotelOnboardingApplication[]>([])
const total = ref(0)
const query = reactive({ pageNo: 1, pageSize: 10 })
const formVisible = ref(false)
const materialsVisible = ref(false)
const viewMaterials = ref<HotelQualificationMaterial[]>([])
const formRef = ref<FormInstance>()
const form = reactive({
  id: undefined as string | number | undefined,
  hotelName: '',
  propertyType: 0,
  address: '',
  contactName: '',
  contactPhone: '',
  materials: [{ name: '', url: '' }] as HotelQualificationMaterial[]
})
const rules: FormRules = {
  hotelName: [{ required: true, message: '请输入酒店名称', trigger: 'blur' }],
  propertyType: [{ required: true, message: '请选择酒店业态', trigger: 'change' }],
  address: [{ required: true, message: '请输入经营地址', trigger: 'blur' }],
  contactName: [{ required: true, message: '请输入联系人', trigger: 'blur' }],
  contactPhone: [{ required: true, message: '请输入联系电话', trigger: 'blur' }]
}

const statusMeta = (status: HotelOnboardingStatus) => statusMap[status] || statusMap.DRAFT
const propertyTypeLabel = (value: number) =>
  propertyTypes.find((item) => item.value === value)?.label || '-'

function parseMaterials(value?: string): HotelQualificationMaterial[] {
  try {
    const parsed = JSON.parse(value || '{}')
    return Array.isArray(parsed.materials)
      ? parsed.materials.filter((item: any) => item?.name && item?.url)
      : []
  } catch {
    return []
  }
}

function safeMaterialUrl(value: string) {
  return /^https?:\/\//i.test(value) ? value : undefined
}

async function loadList() {
  loading.value = true
  try {
    const data: any = await getMerchantOnboardingPage(query)
    list.value = data?.list || []
    total.value = Number(data?.total || 0)
  } finally {
    loading.value = false
  }
}

function openForm(row?: HotelOnboardingApplication) {
  form.id = row?.id
  form.hotelName = row?.hotelName || ''
  form.propertyType = row?.propertyType ?? 0
  form.address = row?.address || ''
  form.contactName = row?.contactName || ''
  form.contactPhone = row?.contactPhone || ''
  form.materials = row ? parseMaterials(row.qualificationJson) : [{ name: '', url: '' }]
  if (form.materials.length === 0) form.materials = [{ name: '', url: '' }]
  formVisible.value = true
}

function addMaterial() {
  form.materials.push({ name: '', url: '' })
}

function removeMaterial(index: number) {
  form.materials.splice(index, 1)
  if (form.materials.length === 0) addMaterial()
}

async function saveForm() {
  await formRef.value?.validate()
  const materials = form.materials
    .map((item) => ({ name: item.name.trim(), url: item.url.trim() }))
    .filter((item) => item.name || item.url)
  if (
    materials.length === 0 ||
    materials.some((item) => !item.name || !/^https?:\/\//i.test(item.url))
  ) {
    ElMessage.warning('请完整填写至少一项资质名称和 http(s) 原件 URL')
    return
  }
  saving.value = true
  try {
    const data = {
      id: form.id,
      hotelName: form.hotelName.trim(),
      propertyType: form.propertyType,
      address: form.address.trim(),
      contactName: form.contactName.trim(),
      contactPhone: form.contactPhone.trim(),
      qualificationJson: JSON.stringify({ materials })
    }
    if (form.id) await updateMerchantOnboarding(data)
    else await createMerchantOnboarding(data)
    ElMessage.success('草稿已保存')
    formVisible.value = false
    await loadList()
  } finally {
    saving.value = false
  }
}

async function submitApplication(row: HotelOnboardingApplication) {
  await ElMessageBox.confirm(`确认提交“${row.hotelName}”的酒店入驻审核吗？`, '提交确认', {
    type: 'warning'
  })
  await submitMerchantOnboarding(row.id)
  ElMessage.success('已提交平台审核')
  await loadList()
}

function showMaterials(row: HotelOnboardingApplication) {
  viewMaterials.value = parseMaterials(row.qualificationJson)
  materialsVisible.value = true
}

onMounted(loadList)
</script>

<style scoped lang="scss">
.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;

  h2 {
    margin: 0 0 6px;
  }

  p {
    margin: 0;
    color: #909399;
  }
}

.materials {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 10px;
}

.material-row {
  display: grid;
  grid-template-columns: 180px 1fr 44px;
  gap: 8px;
}
</style>
